import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as uiSlice from '../../../../data/ui.slice';
import * as clientSlice from '../../../../data/client.slice';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import PassengerIngestionServerConfigForm from './PassengerIngestionServerConfigForm';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { PassengerIngestionServerConfiguration } from '../../../../data/models/passenger-ingestion-server-configuration.model';
import clientUtils from '../../../../services/clientUtils';

const PREFIX = 'PassengerIngestionServerConfigDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface PassengerIngestionServerConfigDialogProps {
  clientBC: string;
  open: boolean;
  serverConfig?: PassengerIngestionServerConfiguration;
  close: () => void;
}

export default function PassengerIngestionServerConfigDialog({
  clientBC,
  open,
  serverConfig,
  close,
}: PassengerIngestionServerConfigDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const savingConfig = useAppSelector(
    clientSlice.selectSavingPassengerIngestionServerConfig,
  );

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: serverConfig,
  });

  useEffect(() => {
    if (serverConfig) {
      formHook.reset(serverConfig);
    }
  }, [serverConfig]);

  useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  const handleClose = () => {
    formHook.reset(serverConfig);
    close();
  };

  const handleSave = (data: any) => {
    if (serverConfig) {
      dispatch(
        clientSlice.updatePassengerIngestionServerConfigAsync(
          clientUtils.getEditablePassengerIngestionServerConfiguration(
            clientBC,
            serverConfig,
            data,
          ),
        ),
      );
    } else {
      dispatch(
        clientSlice.createPassengerIngestionServerConfigAsync(
          clientUtils.getCreatablePassengerIngestionServerConfiguration(
            clientBC,
            data,
          ),
        ),
      );
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {serverConfig
          ? t('client.editServerConfig')
          : t('client.configureMemberIngestion')}
        {savingConfig && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <PassengerIngestionServerConfigForm
            isNew={!serverConfig}
            formHook={formHook}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            !formHook.formState.isDirty ||
            savingConfig
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
